import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flashbar,
  Header,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  Spinner,
  Table,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { EmptyState } from 'src/commons/EmptyState';
import { createDatasetDetailLink } from 'src/routes';
import { Link } from 'src/components/common/Link';
import { listClassifications, updateClassification } from 'src/api/classify';

interface DatasetClassification {
  id: string;
  catalogName: string;
  databaseName: string;
  tableName: string;
  classification: string;
  reviewStatus: string;
  lastUpdated: string;
  owner: string;
  dataSetName: string;
}

interface ClassificationPageProps {
  activeGroup?: string;
  activeWorkspace?: any;
  toggleHelp?: () => void;
}

export const ClassificationPage = (props: ClassificationPageProps) => {
  const [datasets, setDatasets] = useState<DatasetClassification[]>([]);
  const [filteredDatasets, setFilteredDatasets] = useState<DatasetClassification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [classificationFilter, setClassificationFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [selectedItems, setSelectedItems] = useState<DatasetClassification[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMoreData, setHasMoreData] = useState<boolean>(false);
  const [sortingColumn, setSortingColumn] = useState<any>(null);
  const [sortingDescending, setSortingDescending] = useState<boolean>(false);
  const [showAcceptAllConfirm, setShowAcceptAllConfirm] = useState<boolean>(false);
  const [showBulkAcceptConfirm, setShowBulkAcceptConfirm] = useState<boolean>(false);

  const parseDatasetId = (datasetId: string) => {
    const parts = datasetId.split('|');
    return {
      catalogName: parts.find((p) => p.startsWith('A-'))?.substring(2) || 'Unknown',
      databaseName: parts.find((p) => p.startsWith('DN-'))?.substring(3) || 'Unknown',
      tableName: parts.find((p) => p.startsWith('TN-'))?.substring(3) || 'Unknown',
    };
  };

  const transformDatasetItem = (item: any, currentOwnerId: string): DatasetClassification => {
    const { catalogName, databaseName, tableName } = parseDatasetId(item.datasetId);
    const finalClassification = item.overriddenClassification || item.classification || 'NONE';

    return {
      id: item.datasetId,
      catalogName,
      databaseName,
      tableName,
      dataSetName: tableName,
      classification: finalClassification === 'PERSONAL' ? 'Personal' : 'None',
      reviewStatus: item.reviewStatus,
      lastUpdated: new Date().toISOString().split('T')[0],
      owner: currentOwnerId,
    };
  };

  useEffect(() => {
    fetchDatasets();
  }, [statusFilter, classificationFilter]);

  useEffect(() => {
    filterDatasets();
  }, [datasets, searchText, classificationFilter]);

  useEffect(() => {
    if (props.toggleHelp) {
      props.toggleHelp();
    }
  }, []);

  const fetchDatasets = async (loadMore = false) => {
    setLoading(true);

    if (!loadMore) {
      setSortingColumn(null);
      setSortingDescending(false);
    }

    try {
      const currentOwnerId = props.activeWorkspace?.workspaceId || props.activeGroup;
      if (!currentOwnerId) {
        setDatasets([]);
        return;
      }

      const requestParams: any = { ownerId: currentOwnerId };
      if (statusFilter !== 'all') {
        requestParams.reviewStatus = statusFilter.toUpperCase();
      }
      if (loadMore && nextToken) {
        requestParams.nextToken = nextToken;
      }

      const classificationResponse = await listClassifications(requestParams);

      const transformedDatasets: DatasetClassification[] = classificationResponse.datasetClassifications.map((item) =>
        transformDatasetItem(item, currentOwnerId),
      );

      if (loadMore) {
        setDatasets((prev) => [...prev, ...transformedDatasets]);
      } else {
        setDatasets(transformedDatasets);
      }

      setNextToken(classificationResponse.nextToken || null);
      setHasMoreData(!!classificationResponse.nextToken);
    } catch (error) {
      console.error('Failed to fetch datasets:', error);
      setNotifications([
        {
          type: 'error',
          content: 'Failed to load classification data',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          id: 'fetch-error',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filterDatasets = () => {
    let filtered = datasets;

    // Filter by search text only (status filtering is handled by API)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((dataset) => {
        const fullTableName = `${dataset.catalogName}.${dataset.databaseName}.${dataset.tableName}`;
        return fullTableName.toLowerCase().includes(searchLower);
      });
    }

    // Filter by classification
    if (classificationFilter !== 'all') {
      filtered = filtered.filter((dataset) => {
        if (classificationFilter === 'personal') {
          return dataset.classification === 'Personal';
        } else if (classificationFilter === 'none') {
          return dataset.classification === 'None';
        }
        return true;
      });
    }

    setFilteredDatasets(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getFullTableName = (dataset: DatasetClassification) => {
    return `${dataset.catalogName}.${dataset.databaseName}.${dataset.tableName}`;
  };

  const handleBulkAccept = async () => {
    if (selectedItems.length > 0) {
      setLoading(true);
      try {
        // Filter only pending datasets for bulk accept
        const pendingItems = selectedItems.filter((item) => item.reviewStatus.toUpperCase() === 'PENDING');

        if (pendingItems.length === 0) {
          setNotifications([
            {
              type: 'warning',
              content: 'No pending classifications to accept',
              dismissible: true,
              onDismiss: () => setNotifications([]),
              id: 'no-pending-warning',
            },
          ]);
          return;
        }

        // Call updateClassification individually for each pending dataset
        const results = await Promise.allSettled(
          pendingItems.map((selectedItem) => {
            const updatePayload = {
              datasetId: selectedItem.id,
              reviewStatus: 'ACCEPTED',
              columns: [],
            };
            return updateClassification(updatePayload).then(() => selectedItem.id);
          }),
        );

        const successfulIds = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
        const failedCount = results.filter((result) => result.status === 'rejected').length;

        // Update local state only for successful items
        const successfulIdsSet = new Set(successfulIds);
        const updatedDatasets = datasets.map((dataset) => {
          if (successfulIdsSet.has(dataset.id)) {
            return { ...dataset, reviewStatus: 'ACCEPTED' };
          }
          return dataset;
        });

        setDatasets(updatedDatasets);
        setSelectedItems([]);

        const notifications = [];
        if (successfulIds.length > 0) {
          notifications.push({
            type: 'success',
            content: `Successfully accepted ${successfulIds.length} classification${
              successfulIds.length > 1 ? 's' : ''
            }`,
            dismissible: true,
            onDismiss: () => setNotifications([]),
            id: 'bulk-accept-success',
          });
        }
        if (failedCount > 0) {
          notifications.push({
            type: 'error',
            content: `Failed to accept ${failedCount} classification${failedCount > 1 ? 's' : ''}`,
            dismissible: true,
            onDismiss: () => setNotifications([]),
            id: 'bulk-accept-partial-error',
          });
        }
        setNotifications(notifications);
      } catch (error) {
        console.error('Failed to bulk accept classifications:', error);
        setNotifications([
          {
            type: 'error',
            content: 'Failed to accept selected classifications',
            dismissible: true,
            onDismiss: () => setNotifications([]),
            id: 'bulk-accept-error',
          },
        ]);
      } finally {
        setLoading(false);
      }
    }
  };

  //Fetches all datasets across all pages for bulk operations
  const fetchAllDatasets = async (): Promise<DatasetClassification[]> => {
    const currentOwnerId = props.activeWorkspace?.workspaceId || props.activeGroup;
    if (!currentOwnerId) {
      throw new Error('No owner ID available');
    }

    const allDatasets: DatasetClassification[] = [];
    let currentToken: string | null = null;

    do {
      const requestParams: any = { ownerId: currentOwnerId };
      if (currentToken) {
        requestParams.nextToken = currentToken;
      }

      const classificationResponse = await listClassifications(requestParams);

      const transformedDatasets = classificationResponse.datasetClassifications.map((item) =>
        transformDatasetItem(item, currentOwnerId),
      );

      allDatasets.push(...transformedDatasets);
      currentToken = classificationResponse.nextToken || null;
    } while (currentToken);

    return allDatasets;
  };

  /**
   * Accepts all non-personal data classifications across all pages
   */
  const handleAcceptAllNonPersonal = async () => {
    setLoading(true);
    try {
      // Fetch all datasets using extracted helper
      const allDatasets = await fetchAllDatasets();

      // Filter for pending non-personal data only
      const nonPersonalPending = allDatasets.filter(
        (item) => item.reviewStatus.toUpperCase() === 'PENDING' && item.classification === 'None',
      );

      if (nonPersonalPending.length === 0) {
        setNotifications([
          {
            type: 'info',
            content: 'No pending non-personal data classifications found',
            dismissible: true,
            onDismiss: () => setNotifications([]),
            id: 'no-non-personal-pending',
          },
        ]);
        return;
      }

      const results = await Promise.allSettled(
        nonPersonalPending.map((selectedItem) => {
          const updatePayload = {
            datasetId: selectedItem.id,
            reviewStatus: 'ACCEPTED',
            columns: [],
          };
          return updateClassification(updatePayload).then(() => selectedItem.id);
        }),
      );

      const successfulIds = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      const failedCount = results.filter((result) => result.status === 'rejected').length;

      // Update local state using existing pattern
      const successfulIdsSet = new Set(successfulIds);
      const updatedDatasets = datasets.map((dataset) => {
        if (successfulIdsSet.has(dataset.id)) {
          return { ...dataset, reviewStatus: 'ACCEPTED' };
        }
        return dataset;
      });

      setDatasets(updatedDatasets);

      const notifications = [];
      if (successfulIds.length > 0) {
        notifications.push({
          type: 'success',
          content: `Successfully accepted ${successfulIds.length} non-personal data classification${
            successfulIds.length > 1 ? 's' : ''
          }`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
          id: 'bulk-accept-non-personal-success',
        });
      }
      if (failedCount > 0) {
        notifications.push({
          type: 'error',
          content: `Failed to accept ${failedCount} classification${failedCount > 1 ? 's' : ''}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
          id: 'bulk-accept-non-personal-error',
        });
      }
      setNotifications(notifications);
    } catch (error) {
      console.error('Failed to accept all non-personal data:', error);
      setNotifications([
        {
          type: 'error',
          content: 'Failed to accept non-personal data classifications',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          id: 'bulk-accept-non-personal-failed',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualAccept = async (action: string) => {
    const dataset = datasets.find((d) => d.id === action);
    if (dataset?.reviewStatus.toUpperCase() !== 'PENDING') {
      return;
    }

    setLoading(true);
    try {
      const updatePayload = {
        datasetId: action,
        reviewStatus: 'ACCEPTED',
        columns: [],
      };

      await updateClassification(updatePayload);

      // Update local state
      setDatasets((prev) =>
        prev.map((dataset) => (dataset.id === action ? { ...dataset, reviewStatus: 'ACCEPTED' } : dataset)),
      );

      setNotifications([
        {
          type: 'success',
          content: 'Classification accepted successfully',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          id: 'individual-accept-success',
        },
      ]);
    } catch (error) {
      console.error('Failed to accept classification:', error);
      setNotifications([
        {
          type: 'error',
          content: 'Failed to accept classification',
          dismissible: true,
          onDismiss: () => setNotifications([]),
          id: 'individual-accept-error',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'Pending';
      case 'ACCEPTED':
        return 'Accepted';
      case 'OVERRIDDEN':
        return 'Overridden';
      default:
        return 'Unknown';
    }
  };

  const getClassificationText = (classification: string) => {
    return classification === 'Personal' ? 'Personal' : 'None';
  };

  const datasetTableColumns: TableProps.ColumnDefinition<DatasetClassification>[] = [
    {
      id: 'tableName',
      header: 'Table name',
      cell: (item: DatasetClassification) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <Link href={`${createDatasetDetailLink(item.id)}?tab=Classification`} external>
            {getFullTableName(item)}
          </Link>
        </div>
      ),
      minWidth: 300,
      sortingField: 'tableName',
      isRowHeader: true,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: DatasetClassification) => getStatusText(item.reviewStatus),
      minWidth: 120,
      sortingField: 'reviewStatus',
    },
    {
      id: 'classification',
      header: 'Classification',
      cell: (item: DatasetClassification) => getClassificationText(item.classification),
      minWidth: 150,
      sortingField: 'classification',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item: DatasetClassification) => (
        <div style={{ whiteSpace: 'nowrap', minWidth: '140px' }}>
          {item.reviewStatus.toUpperCase() === 'PENDING' ? (
            <Button variant='normal' onClick={() => handleIndividualAccept(item.id)} loading={loading}>
              Quick Accept
            </Button>
          ) : null}
        </div>
      ),
      minWidth: 160,
    },
  ];

  const overviewContent = (
    <SpaceBetween size='m' direction='vertical'>
      <Container header={<Header>Table Classification</Header>}>
        <SpaceBetween size='m' direction='vertical'>
          <Box>
            <TextContent>Review and manage classification of tables</TextContent>
          </Box>

          {/* Search and Filter Controls */}
          <SpaceBetween size='s' direction='horizontal'>
            <TextFilter
              filteringText={searchText}
              onChange={({ detail }) => setSearchText(detail.filteringText)}
              countText={`${filteredDatasets.length} matches`}
              filteringPlaceholder='Find tables'
            />
            <Select
              selectedOption={{
                label:
                  statusFilter === 'all'
                    ? 'All statuses'
                    : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1),
                value: statusFilter,
              }}
              onChange={({ detail }) => setStatusFilter(detail.selectedOption.value)}
              options={[
                { label: 'All statuses', value: 'all' },
                { label: 'Pending', value: 'pending' },
                { label: 'Accepted', value: 'accepted' },
                { label: 'Overridden', value: 'overridden' },
              ]}
            />
            <Select
              selectedOption={{
                label:
                  classificationFilter === 'all'
                    ? 'All classifications'
                    : classificationFilter === 'personal'
                    ? 'Personal'
                    : 'None',
                value: classificationFilter,
              }}
              onChange={({ detail }) => setClassificationFilter(detail.selectedOption.value)}
              options={[
                { label: 'All classifications', value: 'all' },
                { label: 'Personal', value: 'personal' },
                { label: 'None', value: 'none' },
              ]}
            />
            <Button variant='normal' iconName='refresh' onClick={() => fetchDatasets()} loading={loading}></Button>
          </SpaceBetween>

          {loading ? (
            <Box textAlign='center' padding='l'>
              <Spinner size='large' />
            </Box>
          ) : (
            <>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <Table
                  columnDefinitions={datasetTableColumns}
                  items={filteredDatasets.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                  sortingColumn={sortingColumn}
                  sortingDescending={sortingDescending}
                  onSortingChange={({ detail }) => {
                    setSortingColumn(detail.sortingColumn);
                    setSortingDescending(detail.isDescending);

                    // Sort the filtered datasets
                    const sortedDatasets = [...filteredDatasets].sort((a, b) => {
                      const field = detail.sortingColumn.sortingField;
                      let aValue = field === 'tableName' ? getFullTableName(a) : a[field];
                      let bValue = field === 'tableName' ? getFullTableName(b) : b[field];

                      if (typeof aValue === 'string') {
                        aValue = aValue.toLowerCase();
                        bValue = bValue.toLowerCase();
                      }

                      if (aValue < bValue) return detail.isDescending ? 1 : -1;
                      if (aValue > bValue) return detail.isDescending ? -1 : 1;
                      return 0;
                    });

                    setFilteredDatasets(sortedDatasets);
                    setCurrentPage(1);
                  }}
                  variant='container'
                  selectionType='multi'
                  selectedItems={selectedItems}
                  onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                  resizableColumns
                  header={
                    <Header
                      counter={filteredDatasets?.length ? `(${filteredDatasets.length})` : '(0)'}
                      actions={
                        <SpaceBetween size='s' direction='horizontal'>
                          {filteredDatasets.length > 0 && (
                            <Pagination
                              currentPageIndex={currentPage}
                              pagesCount={Math.ceil(filteredDatasets.length / pageSize)}
                              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                            />
                          )}

                          <Button variant='primary' onClick={() => setShowAcceptAllConfirm(true)} loading={loading}>
                            Accept all non-personal data
                          </Button>
                          {selectedItems.length > 0 && (
                            <Button
                              variant='primary'
                              onClick={() => setShowBulkAcceptConfirm(true)}
                              disabled={selectedItems.length === 0}
                            >
                              Accept selected ({selectedItems.length})
                            </Button>
                          )}
                        </SpaceBetween>
                      }
                    >
                      Tables
                    </Header>
                  }
                  empty={<EmptyState title='No tables' subtitle='No tables available for classification.' />}
                />
              </div>

              {hasMoreData && (
                <Box textAlign='center' padding='m'>
                  <Button onClick={() => fetchDatasets(true)} loading={loading} variant='normal'>
                    Load more datasets
                  </Button>
                </Box>
              )}
            </>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );

  return (
    <SpaceBetween size='m' direction='vertical'>
      <Header variant='h1' description='Manage and classify your data resources'>
        Dataset Classification - <i>Preview</i>
      </Header>

      {notifications.length > 0 && <Flashbar items={notifications} />}

      <Modal
        visible={showAcceptAllConfirm}
        onDismiss={() => setShowAcceptAllConfirm(false)}
        closeAriaLabel='Close modal'
        size='medium'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='link' onClick={() => setShowAcceptAllConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant='primary'
                loading={loading}
                onClick={() => {
                  handleAcceptAllNonPersonal();
                  setShowAcceptAllConfirm(false);
                }}
              >
                Accept all
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Accept all non-personal data classifications?'
      >
        <SpaceBetween size='m'>
          <Box variant='p'>
            This will accept all pending non-personal data classifications across all pages, not just the currently
            filtered or visible items.
          </Box>
          {(classificationFilter !== 'all' || searchText) && (
            <Box variant='p' color='text-status-warning'>
              Note: You have active filters applied, but this action will process all data regardless of your current
              filter settings.
            </Box>
          )}
          <Box variant='p'>
            <strong>This action cannot be undone.</strong>
          </Box>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={showBulkAcceptConfirm}
        onDismiss={() => setShowBulkAcceptConfirm(false)}
        closeAriaLabel='Close modal'
        size='medium'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button variant='link' onClick={() => setShowBulkAcceptConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant='primary'
                loading={loading}
                onClick={() => {
                  handleBulkAccept();
                  setShowBulkAcceptConfirm(false);
                }}
              >
                Accept {selectedItems.length} classification{selectedItems.length > 1 ? 's' : ''}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Accept selected classifications?'
      >
        <SpaceBetween size='m'>
          <Box variant='p'>
            This will accept {selectedItems.length} selected classification{selectedItems.length > 1 ? 's' : ''}.
          </Box>
          <Box variant='p'>
            <strong>This action cannot be undone.</strong>
          </Box>
        </SpaceBetween>
      </Modal>

      {overviewContent}
    </SpaceBetween>
  );
};
